import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { MainPageBase } from "../page-bases/MainPageBase";
import { Box, Divider, Stack } from "@mui/material";
import dayjs from "dayjs";
import { IAssetMaintenanceSchedule } from "../../config/types";
import { ExpandableContainer } from "../../components/Collapsible/ExpandableContainer";
import ItemInput from "../../components/Input/ItemInput";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { DatePicker } from "../../components/calender";
import VerticalGap from "../../components/Gap/VerticalGap";
import { actionDialogStore, alertDialogStore } from "../../store/dialogs";
import useAppNavigation from "../../hooks/useAppNavigation";
import appRoutePath from "../../config/path";
import ItemSelectInput from "../../components/Input/ItemSelectInput";
import { ASSET_SCHEDULE_STATUS } from "../../config/constants/common";
import { AssetMaintenanceApi } from "../../services/maintenance";
import DamsTable from "../../components/Table/CollapsibleTable";
import { TRowItem } from "../../components/Table/TableRow";
import { assetApi } from "../../services/assets/asset-crud";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { usePagination } from "../../hooks/usePagination";
import { IAssetRecord, IPagination, IQueryPerams } from "../../config/types";
import { formatDate } from "../../utils/common";
import { AssetLogsFilter } from "../../common-ui/page-filters";

const defaultValue = <T,>(value: T) => ({ isValid: true, value });

function UpdateAssetMaintenanceSchedule() {
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const [maintenanceData, setMaintenanceData] =
    useState<IAssetMaintenanceSchedule | null>(null);

  const [searchParams] = useSearchParams();
  const showAssetsTable = searchParams.get("showAssets") === "true";

  const {
    form,
    updateForm,
    updateInputField,
    fieldHasError,
    setSubmitAttempt,
    onFormSubmit,
  } = useDmsForm<IAssetMaintenanceSchedule>({
    service_date: "",
    service_remarks: "",
    schedule_status: "",
    assets: [],
  });

  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
  } = useMangeTableItems(dataset);

  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<any>(assetApi.getAssets);

  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });
  const navigate = useAppNavigation();
  const target = useParams<{ id: string }>();

  useEffect(() => {
    const fetchMaintenanceDetail = async () => {
      if (!target?.id) return;

      const res = await AssetMaintenanceApi.getAssetMaintenanceSchedule(
        target.id
      );
      if (res.data) {
        const data = res.data;
        setMaintenanceData(data);

        updateForm({
          service_date: defaultValue(data.service_date),
          service_remarks: defaultValue(data.service_remarks),
          schedule_status: defaultValue(data.schedule_status),
          assets: defaultValue(data.assets || []),
        });
      }
    };
    fetchMaintenanceDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.id]);

  const fetchAssetsData = async () => {
    if (!showAssetsTable) return;

    const result = await assetApi.getAssets({ is_deleted: false });
    if (result?.data) {
      populateDataSet(result.data, result.pagination);
    }
  };

  const populateDataSet = (data: IAssetRecord[], pagination?: IPagination) => {
    const formatData = data.map((data) => ({
      ...data,
      brand: `${data.brand}(${data.model})`,
      date_received: formatDate(data.date_received),
      warranty_in_months: data.warranty_in_months ?? "Not Stated",
      asset_meta_raw: data.asset_meta
        ? data.asset_meta.map(
            (item) => ` ${item.asset_tag}=${item.meta_value} `
          )
        : [],
    }));
    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  useEffect(() => {
    fetchAssetsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAssetsTable]);

  useEffect(() => {
    if (showAssetsTable && form.assets.value.length > 0 && dataset.length > 0) {
      const preSelectedItems = dataset.filter((item) =>
        form.assets.value.includes(item.id)
      );
      preSelectedItems.forEach((item) => toggleRow(item));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.assets.value, dataset, showAssetsTable]);

  useEffect(() => {
    if (showAssetsTable) {
      updateForm((prev) => ({
        ...prev,
        assets: {
          value: selectedRowItems.map((row) => row.id),
          isValid: selectedRowItems.length > 0,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowItems.length, showAssetsTable]);

  function cancelUpdate() {
    alertDialogStore.open({
      title: "Unsaved Changes",
      subtitle: `Do you want to discard changes?`,
      onRightBtnPress: () =>
        navigate({ path: appRoutePath.ASSET_MAINTENANCE_HISTORY_PAGE }),
      isSuccess: false,
    });
  }

  async function toggleUpdateBtn() {
    setSubmitAttempt(true);
    const { data, isValid } = onFormSubmit();

    if (isValid) {
      const updateRequestFtn = async () => {
        const response =
          await AssetMaintenanceApi.updateAssetMaintenanceSchedule(
            target.id ?? "",
            data
          );
        alertDialogStore.open({
          title: "Update Maintenance Schedule",
          subtitle: response.message,
          rightBtnTxt: `${
            response.success ? "See All Schedules" : "Try again"
          }`,
          onRightBtnPress: async () => {
            if (response.success) {
              navigate({ path: appRoutePath.ASSET_MAINTENANCE_HISTORY_PAGE });
            }
          },
          isSuccess: response.success,
        });
      };

      actionDialogStore.open({
        title: "Update Maintenance Schedule",
        subtitle: 'Click "Confirm" to update maintenance schedule',
        onRightBtnPress: updateRequestFtn,
      });
    }
  }

  const handleDatePicker = (date: string) => {
    updateForm((p) => ({
      ...p,
      service_date: { value: date, isValid: true, errorMessage: "" },
    }));
  };

  if (target.id && !maintenanceData) {
    return null;
  }

  return (
    <MainPageBase
      title={"Update Maintenance Schedule"}
      pageState={"add-item"}
      cancelBtnClick={cancelUpdate}
      addItemBtnOnClick={toggleUpdateBtn}
    >
      <Box height={`100%`} display={"flex"} justifyContent={"center"}>
        <Box width={"100%"} m={"0px 32px"}>
          <Stack direction={"row"} gap={4}>
            <ExpandableContainer
              label="Maintenance Schedule Information"
              initOpenState={true}
            >
              <Stack gap={3} pb={3} pr={3} minHeight={350}>
                <Stack pt={3} gap={4} direction={"row"}>
                  <DatePicker
                    handleOnchange={handleDatePicker}
                    label="Service Date"
                    value={dayjs(form.service_date.value)}
                    error={fieldHasError("service_date")}
                    errorMessage={form.service_date.errorMessage}
                    required={true}
                  />

                  <ItemSelectInput
                    label="Schedule Status"
                    placeholder="Select Schedule Status"
                    items={ASSET_SCHEDULE_STATUS}
                    item=""
                    value={form.schedule_status.value}
                    error={fieldHasError("schedule_status")}
                    handleChange={updateInputField("schedule_status")}
                    errorMessage={form.schedule_status.errorMessage}
                    required={true}
                  />
                </Stack>

                <ItemInput
                  label="Service Remarks"
                  placeholder="Long text"
                  handleChange={updateInputField("service_remarks")}
                  value={form.service_remarks.value}
                  error={fieldHasError("service_remarks")}
                  errorMessage={form.service_remarks.errorMessage}
                  size="full"
                  multiline
                  required={true}
                />
              </Stack>
            </ExpandableContainer>
          </Stack>

          {showAssetsTable && (
            <>
              <Divider variant="fullWidth" />

              <Stack width={"100%"}>
                <ExpandableContainer
                  label="Assets Table"
                  isTable={true}
                  initOpenState
                >
                  <VerticalGap height={20} />

                  <AssetLogsFilter<IAssetRecord[]>
                    ftn={assetApi.getAssets}
                    updateDataSet={populateDataSet}
                    updateQuerySet={setQuerySet}
                  />

                  <DamsTable
                    tableTitle="Assets List"
                    tableSubtitle="Select assets for this maintenance schedule"
                    useTableCheckbox={true}
                    selectedRowItems={selectedRowItems}
                    tableHeaders={columns}
                    tableKeys={tkeys}
                    hiddenKeys={hkeys}
                    hiddenLabels={hcolumns}
                    data={dataset}
                    maxRowCells={6}
                    onTableItemSelect={toggleRow}
                    toggleSelectAllRow={toggleSelectAllRow}
                    hasPagination
                    paginationState={pagination}
                    setActivePage={paginateToPageNumber(
                      populateDataSet,
                      querySet
                    )}
                    onNextPage={paginateToNextPage(querySet, populateDataSet)}
                    onPreviousPage={paginateToPreviousPage(
                      querySet,
                      populateDataSet
                    )}
                    tableTitleBadge={
                      selectedRowItems.length === 0
                        ? undefined
                        : selectedRowItems.length + " selected"
                    }
                  />
                  <VerticalGap height={30} />
                </ExpandableContainer>
              </Stack>
            </>
          )}
        </Box>
      </Box>
    </MainPageBase>
  );
}

const tkeys = [
  "asset_category_name",
  "serial_number",
  "brand",
  "embossment_number",
  "assigned",
  "date_received",
];

const columns = [
  "Asset Category",
  "Serial Number",
  "Brand (Model)",
  "Embossment Number",
  "Assignment Status",
  "Date Received",
];

const hkeys: (keyof IAssetRecord)[] = [
  "batch_number",
  "current_location",
  "current_location_domain",
  "description",
  "warranty_in_months",
  "asset_meta_raw",
];

const hcolumns = [
  "Batch Number",
  "Current Location",
  "current location domain",
  "Description",
  "Warranty (months)",
  "Additional Information",
];

export { UpdateAssetMaintenanceSchedule };
